/**
 * PaymentScheduleBulkModal — bulk-create N milestones for an invoice.
 *
 * Presets: 30/60/10 Solar Default (visually default-selected), 50/50,
 * 100% upfront, Custom. Live sum-validation; submit blocked unless
 * sum equals invoice total ± 0.01.
 *
 * Sun Stellar's primary use-case is the 30/60/10 split for solar +
 * heat-pump quotes (advance / dispatch / commissioning).
 */
import { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { formatINR } from '@/utils/formatters';
import type { NewMilestoneInput } from '@/hooks/usePaymentSchedules';

type PresetKey = '30-60-10' | '50-50' | '100' | 'custom';

interface DraftRow {
  description: string;
  dueDate: string; // yyyy-mm-dd
  dueAmount: number;
}

interface Props {
  open: boolean;
  invoiceTotal: number;
  onClose: () => void;
  onSubmit: (inputs: NewMilestoneInput[]) => Promise<unknown>;
}

function isoOffset(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Round HALF_UP to 2dp without floating-point surprise. */
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

function buildPreset(preset: PresetKey, total: number): DraftRow[] {
  if (preset === '30-60-10') {
    const a = r2(total * 0.3);
    const b = r2(total * 0.6);
    // Last row absorbs paise drift so sum is exact.
    const c = r2(total - a - b);
    return [
      { description: 'Advance on PO', dueDate: isoOffset(7), dueAmount: a },
      { description: 'On dispatch', dueDate: isoOffset(14), dueAmount: b },
      { description: 'On commissioning', dueDate: isoOffset(30), dueAmount: c },
    ];
  }
  if (preset === '50-50') {
    const a = r2(total * 0.5);
    const b = r2(total - a);
    return [
      { description: 'Advance', dueDate: isoOffset(7), dueAmount: a },
      { description: 'On delivery', dueDate: isoOffset(30), dueAmount: b },
    ];
  }
  if (preset === '100') {
    return [
      { description: 'Full payment', dueDate: isoOffset(7), dueAmount: r2(total) },
    ];
  }
  // Custom — start with one blank row
  return [{ description: '', dueDate: isoOffset(7), dueAmount: 0 }];
}

export function PaymentScheduleBulkModal({
  open,
  invoiceTotal,
  onClose,
  onSubmit,
}: Props) {
  // Default-select 30/60/10 (Sun Stellar core flow per CLAUDE.md).
  const [preset, setPreset] = useState<PresetKey>('30-60-10');
  const [rows, setRows] = useState<DraftRow[]>(() =>
    buildPreset('30-60-10', invoiceTotal)
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset rows whenever modal opens or invoice total changes.
  useEffect(() => {
    if (open) {
      setRows(buildPreset(preset, invoiceTotal));
      setSubmitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceTotal]);

  const handlePreset = (p: PresetKey) => {
    setPreset(p);
    setRows(buildPreset(p, invoiceTotal));
  };

  const handleRowChange = (idx: number, patch: Partial<DraftRow>) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );
    setPreset('custom');
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { description: '', dueDate: isoOffset(30), dueAmount: 0 },
    ]);
    setPreset('custom');
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setPreset('custom');
  };

  /** Balance last row so sum matches invoiceTotal exactly. */
  const autoBalance = () => {
    if (rows.length === 0) return;
    const head = rows.slice(0, -1);
    const headSum = head.reduce((s, r) => s + (r.dueAmount || 0), 0);
    const lastAmount = r2(invoiceTotal - headSum);
    setRows([...head, { ...rows[rows.length - 1]!, dueAmount: lastAmount }]);
    setPreset('custom');
  };

  const sum = useMemo(
    () => rows.reduce((s, r) => s + (r.dueAmount || 0), 0),
    [rows]
  );
  const delta = r2(invoiceTotal - sum);
  const sumOk = Math.abs(delta) < 0.01;
  const anyEmptyDesc = rows.some((r) => !r.description.trim());
  const anyNonPositive = rows.some((r) => !(r.dueAmount > 0));

  const canSubmit =
    rows.length > 0 && sumOk && !anyNonPositive && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: NewMilestoneInput[] = rows.map((r) => ({
        dueDate: r.dueDate,
        dueAmount: r.dueAmount,
        description: r.description.trim() || undefined,
      }));
      await onSubmit(payload);
      onClose();
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to create milestones'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="payment-schedule-bulk-modal">
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Bulk-create payment schedule
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Invoice total: <span className="font-medium text-gray-700">{formatINR(invoiceTotal)}</span>
            </p>
          </div>
          <button
            data-testid="payment-schedule-bulk-modal-close-button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Presets */}
        <div className="mb-5">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Preset
          </div>
          <div className="flex flex-wrap gap-2">
            {([
              { key: '30-60-10' as const, label: '30 / 60 / 10  (Solar default)' },
              { key: '50-50' as const, label: '50 / 50' },
              { key: '100' as const, label: '100% upfront' },
              { key: 'custom' as const, label: 'Custom' },
            ]).map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`payment-schedule-preset-${p.key}`}
                onClick={() => handlePreset(p.key)}
                className={
                  'rounded-lg border px-3 py-1.5 text-sm font-medium transition ' +
                  (preset === p.key
                    ? 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50')
                }
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="mb-3 overflow-hidden rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">Due date</th>
                <th className="px-3 py-2 text-left font-medium">Description</th>
                <th className="px-3 py-2 text-right font-medium">Amount (₹)</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-t border-gray-100" data-testid={`payment-schedule-bulk-row-${idx}`}>
                  <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      data-testid={`payment-schedule-bulk-row-${idx}-due-date-input`}
                      value={row.dueDate}
                      onChange={(e) =>
                        handleRowChange(idx, { dueDate: e.target.value })
                      }
                      className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      data-testid={`payment-schedule-bulk-row-${idx}-description-input`}
                      value={row.description}
                      placeholder="e.g. Advance on PO"
                      onChange={(e) =>
                        handleRowChange(idx, { description: e.target.value })
                      }
                      className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      data-testid={`payment-schedule-bulk-row-${idx}-amount-input`}
                      min={0}
                      step="0.01"
                      value={row.dueAmount}
                      onChange={(e) =>
                        handleRowChange(idx, {
                          dueAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-32 rounded border border-gray-200 px-2 py-1 text-right text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {rows.length > 1 && (
                      <button
                        type="button"
                        data-testid={`payment-schedule-bulk-row-${idx}-remove-button`}
                        onClick={() => removeRow(idx)}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        aria-label={`Remove milestone ${idx + 1}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mb-4 flex items-center justify-between text-xs">
          <button
            type="button"
            data-testid="payment-schedule-bulk-add-row-button"
            onClick={addRow}
            className="rounded border border-gray-200 px-2 py-1 font-medium text-gray-700 hover:bg-gray-50"
          >
            + Add milestone
          </button>
          {!sumOk && (
            <button
              type="button"
              data-testid="payment-schedule-bulk-auto-balance-button"
              onClick={autoBalance}
              className="rounded border border-amber-300 bg-amber-50 px-2 py-1 font-medium text-amber-800 hover:bg-amber-100"
            >
              Auto-balance last milestone
            </button>
          )}
        </div>

        {/* Sum indicator */}
        <div
          className={
            'mb-4 flex items-center justify-between rounded-lg border px-3 py-2 text-sm ' +
            (sumOk
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-amber-300 bg-amber-50 text-amber-900')
          }
        >
          <span className="flex items-center gap-2">
            {sumOk ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <span>
              Scheduled total: <span className="font-semibold">{formatINR(sum)}</span>
              {' '}/ Invoice total: <span className="font-semibold">{formatINR(invoiceTotal)}</span>
            </span>
          </span>
          {!sumOk && (
            <span className="font-medium">
              Δ {formatINR(delta)} {delta > 0 ? 'short' : 'over'}
            </span>
          )}
        </div>

        {anyEmptyDesc && (
          <p className="mb-3 text-xs text-amber-700">
            Tip: add descriptions so the customer sees a clear milestone name on the PDF.
          </p>
        )}
        {anyNonPositive && (
          <p className="mb-3 text-xs text-red-700">
            Every milestone amount must be greater than 0.
          </p>
        )}
        {submitError && (
          <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {submitError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="payment-schedule-bulk-cancel-button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="payment-schedule-bulk-submit-button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className={
              'flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-white ' +
              (canSubmit
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'cursor-not-allowed bg-gray-300')
            }
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Create {rows.length} milestone{rows.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentScheduleBulkModal;
